import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import {
  getMyPartners,
  getPartnerMembers,
  getPartnerTeamStats,
  addPartnerMember,
  updatePartnerMemberRole,
  removePartnerMember,
  getPartnerRoles,
  getUsers,
  getPartners,
} from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const PartnerMembersManagement = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isPlatformAdmin = user?.isAdmin === true;

  // State
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });
  const [search, setSearch] = useState('');
  const [teamStats, setTeamStats] = useState(null);
  const [roles, setRoles] = useState({});

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMemberRole, setNewMemberRole] = useState('ADMIN');
  const [editRole, setEditRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // User search state for autocomplete
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // Fetch partners (all for platform admin, or only memberships for partner users)
  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      if (isPlatformAdmin) {
        // Platform admin: fetch all partners
        const response = await getPartners({ limit: 100 });
        // API returns array directly or wrapped in data property
        const partnersList = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.partners || []);
        // Transform to match the membership structure for consistency
        const transformedPartners = partnersList.map(partner => ({
          partnerId: partner,
          role: 'ADMIN', // Platform admins have admin access
        }));
        setPartners(transformedPartners);
        if (transformedPartners.length > 0 && !selectedPartnerId) {
          const firstPartnerId = transformedPartners[0].partnerId?._id || transformedPartners[0].partnerId;
          setSelectedPartnerId(firstPartnerId);
        }
      } else {
        // Partner member: fetch only their memberships
        const response = await getMyPartners();
        const partnersList = response.data?.memberships || [];
        setPartners(partnersList);
        if (partnersList.length > 0 && !selectedPartnerId) {
          setSelectedPartnerId(partnersList[0].partnerId?._id || partnersList[0].partnerId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      showToast('Failed to fetch partners', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, selectedPartnerId, isPlatformAdmin]);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await getPartnerRoles();
      setRoles(response.data.roles || {});
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  }, []);

  // Fetch members of selected partner
  const fetchMembers = useCallback(async () => {
    if (!selectedPartnerId) return;

    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
      };
      if (search) params.search = search;

      const response = await getPartnerMembers(selectedPartnerId, params);
      setMembers(response.data.members || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
      }));
    } catch (error) {
      showToast('Failed to fetch team members', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedPartnerId, pagination.page, pagination.limit, search, showToast]);

  // Fetch team stats
  const fetchTeamStats = useCallback(async () => {
    if (!selectedPartnerId) return;

    try {
      const response = await getPartnerTeamStats(selectedPartnerId);
      setTeamStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch team stats:', error);
    }
  }, [selectedPartnerId]);

  // Debounce timer ref
  const searchTimeoutRef = useRef(null);

  // Debounced user search function
  const searchUsers = useCallback(async (searchTerm) => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm || searchTerm.length < 2) {
      setUserSearchResults([]);
      setUserSearchLoading(false);
      return;
    }

    setUserSearchLoading(true);

    // Debounce the API call
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await getUsers({ search: searchTerm, limit: 10 });
        setUserSearchResults(response.data?.users || []);
      } catch (error) {
        console.error('Failed to search users:', error);
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle user search input change
  const handleUserSearchInputChange = (event, newInputValue) => {
    setUserSearchInput(newInputValue);
    searchUsers(newInputValue);
  };

  useEffect(() => {
    fetchPartners();
    fetchRoles();
  }, [fetchPartners, fetchRoles]);

  useEffect(() => {
    if (selectedPartnerId) {
      fetchMembers();
      fetchTeamStats();
    }
  }, [selectedPartnerId, fetchMembers, fetchTeamStats]);

  // Handle add member
  const handleAddMember = async () => {
    if (!selectedUser || !newMemberRole) {
      showToast('Please select a user and role', 'error');
      return;
    }

    setActionLoading(true);
    try {
      await addPartnerMember({
        partnerId: selectedPartnerId,
        userId: selectedUser._id,
        role: newMemberRole,
      });
      showToast('Member added successfully', 'success');
      setAddDialogOpen(false);
      setSelectedUser(null);
      setUserSearchInput('');
      setUserSearchResults([]);
      setNewMemberRole('ADMIN');
      fetchMembers();
      fetchTeamStats();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to add member', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update role
  const handleUpdateRole = async () => {
    if (!editRole) {
      showToast('Please select a role', 'error');
      return;
    }

    setActionLoading(true);
    try {
      await updatePartnerMemberRole(selectedMember._id, editRole);
      showToast('Role updated successfully', 'success');
      setEditDialogOpen(false);
      fetchMembers();
      fetchTeamStats();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update role', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle remove member
  const handleRemoveMember = async () => {
    setActionLoading(true);
    try {
      await removePartnerMember(selectedMember._id);
      showToast('Member removed successfully', 'success');
      setDeleteDialogOpen(false);
      fetchMembers();
      fetchTeamStats();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to remove member', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (member) => {
    setSelectedMember(member);
    setEditRole(member.role);
    setEditDialogOpen(true);
  };

  // Open delete dialog
  const handleOpenDeleteDialog = (member) => {
    setSelectedMember(member);
    setDeleteDialogOpen(true);
  };

  // Get role chip
  const getRoleChip = (role) => (
    <Chip label={role} size="small" color="primary" />
  );

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get current user's role in selected partner
  const getCurrentUserRole = () => {
    if (isPlatformAdmin) return 'ADMIN'; // Platform admins have admin-level access
    const membership = partners.find(
      p => (p.partnerId?._id || p.partnerId) === selectedPartnerId
    );
    return membership?.role;
  };

  const canManageMembers = () => {
    if (isPlatformAdmin) return true;
    return getCurrentUserRole() === 'ADMIN';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Team Members
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your partner team members and their roles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchPartners();
              fetchMembers();
              fetchTeamStats();
            }}
          >
            Refresh
          </Button>
          {canManageMembers() && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddDialogOpen(true)}
              disabled={!selectedPartnerId}
            >
              Add Member
            </Button>
          )}
        </Box>
      </Box>

      {/* Partner Selector */}
      {partners.length > 0 ? (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={selectedPartnerId || false}
            onChange={(_, newValue) => {
              setSelectedPartnerId(newValue);
              setPagination(prev => ({ ...prev, page: 0 }));
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {partners.map((membership) => {
              // Handle both formats: populated partnerId object or direct partner object
              const partner = membership.partnerId;
              const partnerId = partner?._id || partner;
              const partnerName = partner?.name || 'Partner';
              return (
                <Tab
                  key={partnerId}
                  value={partnerId}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      {partnerName}
                      {!isPlatformAdmin && (
                        <Chip label={membership.role} size="small" variant="outlined" />
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Paper>
      ) : !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {isPlatformAdmin
            ? 'No partners found. Create a partner first to manage team members.'
            : 'You are not a member of any partner organization. Contact your administrator to get access.'}
        </Alert>
      )}

      {/* Statistics Cards */}
      {teamStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon color="primary" />
                  <Typography variant="h6">{teamStats.total}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettingsIcon color="primary" />
                  <Typography variant="h6">{teamStats.byRole?.ADMIN || 0}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Admins
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setPagination(prev => ({ ...prev, page: 0 }));
                  fetchMembers();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setPagination(prev => ({ ...prev, page: 0 }));
                fetchMembers();
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Members Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Invited By</TableCell>
              <TableCell>Joined</TableCell>
              {canManageMembers() && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !selectedPartnerId ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Select a partner to view members</Typography>
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No members found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {member.userId?.firstName} {member.userId?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.userId?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getRoleChip(member.role)}</TableCell>
                  <TableCell>
                    {member.invitedBy ? (
                      <Typography variant="body2">
                        {member.invitedBy.firstName} {member.invitedBy.lastName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Original
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(member.createdAt)}</TableCell>
                  {canManageMembers() && (
                    <TableCell align="right">
                      <Tooltip title="Edit Role">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(member)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Member">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(member)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Search and select a user to add them to this partner. The user must already have an account on Examinax.
            </Alert>
            <Autocomplete
              fullWidth
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              inputValue={userSearchInput}
              onInputChange={handleUserSearchInputChange}
              options={userSearchResults}
              loading={userSearchLoading}
              getOptionLabel={(option) =>
                option ? `${option.firstName} ${option.lastName} (${option.email})` : ''
              }
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              filterOptions={(x) => x} // Disable built-in filtering since we filter on server
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User"
                  placeholder="Type name or email to search..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {option.firstName?.charAt(0) || <PersonIcon />}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
              noOptionsText={
                userSearchInput.length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No users found'
              }
              sx={{ mb: 2 }}
            />
            {selectedUser && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {selectedUser.firstName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newMemberRole}
                label="Role"
                onChange={(e) => setNewMemberRole(e.target.value)}
              >
                {Object.keys(roles).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>ADMIN:</strong> Full access — can view students, exams, registrations, analytics, and manage team members
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setSelectedUser(null);
            setUserSearchInput('');
            setUserSearchResults([]);
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={actionLoading || !selectedUser || !newMemberRole}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Member Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update role for <strong>{selectedMember?.userId?.firstName} {selectedMember?.userId?.lastName}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                label="Role"
                onChange={(e) => setEditRole(e.target.value)}
              >
                {Object.keys(roles).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateRole}
            disabled={actionLoading || !editRole}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{selectedMember?.userId?.firstName} {selectedMember?.userId?.lastName}</strong> from this partner?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveMember}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerMembersManagement;
